<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { useRestaurantsStore, type Restaurant, type RestaurantLicense } from '@/stores/restaurants'
import { useRestaurantInfoStore } from '@/stores/restaurantInfo'
import { usePermissions } from '@/composables/usePermissions'
import { useAuthStore } from '@/stores/auth'
import { useEffectiveRole } from '@/composables/useEffectiveRole'
import LicensesListModal from '@/components/LicensesListModal.vue'
import ProtectedButton from '@/components/ProtectedButton.vue'
import { BaseButton, BaseInput } from '@/components/base'
import { formatDateOnly } from '@/utils/dateFormat'

const router = useRouter()
const restaurantsStore = useRestaurantsStore()
const restaurantInfoStore = useRestaurantInfoStore()
const { t } = useI18n()
const { can } = usePermissions()
const authStore = useAuthStore()

/** A Datev Accountant reads the list only — no status changes, detail pages or licences. */
const isDatevAccountant = computed(() => authStore.isDatevAccountant)

const { effectiveRole, showPartnerDropdown: showPartnerFilter, showCompanyDropdown: showCompanyFilter } = useEffectiveRole()
const { searchQuery, selectedPartner, selectedCompany, selectedStatus } = storeToRefs(restaurantsStore)
const showDeleteModal = ref(false)
const restaurantToDelete = ref<Restaurant | null>(null)
const isDeleting = ref(false)
const isLoading = ref(false)
const error = ref('')

// Licenses modal
const licensesRestaurant = ref<Restaurant | null>(null)

const openLicensesModal = (restaurant: Restaurant) => {
  licensesRestaurant.value = restaurant
}

onMounted(async () => {
  isLoading.value = true
  error.value = ''
  try {
    await restaurantsStore.fetchRestaurants()
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to load restaurants'
    console.error('Error loading restaurants:', err)
  } finally {
    isLoading.value = false
  }
})

// Reset filters when user role changes
const resetHiddenFilters = () => {
  if (!showPartnerFilter.value) {
    selectedPartner.value = ''
  }
  if (!showCompanyFilter.value) {
    selectedCompany.value = ''
  }
}

watch(effectiveRole, () => {
  resetHiddenFilters()
})

const partnersOptions = computed(() => [
  { label: t('restaurants.filterPartner', 'All Partners'), value: '' },
  ...Array.from(
    new Set(
      restaurantsStore.restaurants
        .filter((r) => r.partner?.company_name)
        .map((r) => r.partner!.company_name)
    )
  )
    .sort()
    .map((name) => ({ label: name, value: name }))
])

const companiesOptions = computed(() => [
  { label: t('restaurants.filterCompany', 'All Companies'), value: '' },
  ...Array.from(
    new Set(
      restaurantsStore.restaurants
        .filter((r) => r.company?.company_name)
        .map((r) => r.company!.company_name)
    )
  )
    .sort()
    .map((name) => ({ label: name, value: name }))
])

const statusOptions = computed(() => [
  { label: t('restaurants.filterStatus'), value: '' },
  { label: t('common.status.active'), value: 'active' },
  { label: t('common.status.inactive'), value: 'inactive' },
])

const hasActiveFilters = computed(
  () => !!searchQuery.value || !!selectedPartner.value || !!selectedCompany.value || selectedStatus.value !== 'active'
)

const resetFilters = () => {
  searchQuery.value = ''
  selectedPartner.value = ''
  selectedCompany.value = ''
  selectedStatus.value = 'active'
}

// Lower rank = shown first: name starts with the query, name contains it,
// address (which ends with the identification ID), then manager / company. -1 = no match.
const searchRank = (r: Restaurant, query: string): number => {
  const name = r.name?.toLowerCase() ?? ''
  if (name.startsWith(query)) return 0
  if (name.includes(query)) return 1
  if (r.address?.toLowerCase().includes(query)) return 2
  if ([r.manager?.name, r.company?.company_name].some((field) => field?.toLowerCase().includes(query))) return 3
  return -1
}

const filteredRestaurants = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()

  const matches = restaurantsStore.restaurants.filter((r) => {
    const matchesPartner = !selectedPartner.value || r.partner?.company_name === selectedPartner.value
    const matchesCompany = !selectedCompany.value || r.company?.company_name === selectedCompany.value
    const matchesStatus = !selectedStatus.value || (selectedStatus.value === 'active' ? r.status === 1 : r.status !== 1)
    return matchesPartner && matchesCompany && matchesStatus
  })

  if (!query) return matches

  // Array.sort is stable, so restaurants with the same rank keep the API order.
  return matches
    .map((r) => ({ r, rank: searchRank(r, query) }))
    .filter(({ rank }) => rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .map(({ r }) => r)
})

const openForm = () => {
  restaurantInfoStore.initializeNew()
  router.push('/onboarding/basic-information')
}

const loadingRestaurantId = ref<string | number | null>(null)

const viewRestaurant = async (restaurant: Restaurant) => {
  loadingRestaurantId.value = restaurant.id
  try {
    await router.push(`/restaurants/${restaurant.id}`)
  } catch {
    // Dynamic import chunk failed (stale cache or network issue) — hard reload to the target URL
    window.location.href = `/restaurants/${restaurant.id}`
  } finally {
    loadingRestaurantId.value = null
  }
}

const confirmDelete = (restaurant: Restaurant) => {
  restaurantToDelete.value = restaurant
  showDeleteModal.value = true
}

const handleDeleteConfirm = async () => {
  if (restaurantToDelete.value) {
    isDeleting.value = true
    error.value = ''
    try {
      await restaurantsStore.deleteRestaurant(String(restaurantToDelete.value.id))
      showDeleteModal.value = false
      restaurantToDelete.value = null
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete restaurant'
      console.error('Error deleting restaurant:', err)
      showDeleteModal.value = false
      restaurantToDelete.value = null
    } finally {
      isDeleting.value = false
    }
  }
}

const handleDeleteCancel = () => {
  showDeleteModal.value = false
  restaurantToDelete.value = null
}

/** A trial is per restaurant: its end date for the "Trial until" tag, else null. */
const restaurantTrialEnd = (restaurant: Restaurant): string | null =>
  restaurant.trial_ends_at ?? null

const getStatusLabel = (status: number) => {
  const statusMap: Record<number, string> = {
    0: t('common.status.inactive'),
    1: t('common.status.active'),
    2: t('common.status.pending'),
  }
  return statusMap[status] || t('common.status.active')
}
</script>

<template>
  <div>
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">{{ t('restaurants.title') }}</h1>
          <p class="text-gray-600 mt-1">{{ t('restaurants.subtitle') }}</p>
        </div>
        <ProtectedButton
          :permission="'restaurant:create'"
          @click="openForm"
          class="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium flex items-center gap-2"
          show-disabled
          :denied-tooltip="t('restaurants.noPermissionCreate')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ t('restaurants.addRestaurant') }}
        </ProtectedButton>
      </div>

      <!-- Search and Filter -->
      <div class="flex gap-4 items-center">
        <BaseInput
          v-model="searchQuery"
          type="search"
          :placeholder="t('restaurants.searchPlaceholder', { count: filteredRestaurants.length })"
          class="max-w-sm"
        />

        <v-select
          v-if="showPartnerFilter"
          v-model="selectedPartner"
          :options="partnersOptions"
          :reduce="option => option.value"
          label="label"
          class="w-full max-w-sm"
        />

        <v-select
          v-if="showCompanyFilter"
          v-model="selectedCompany"
          :options="companiesOptions"
          :reduce="option => option.value"
          label="label"
          class="w-full max-w-sm"
        />

        <v-select
          v-model="selectedStatus"
          :options="statusOptions"
          :reduce="option => option.value"
          :clearable="false"
          label="label"
          class="w-full max-w-[12rem]"
        />

        <BaseButton
          v-if="hasActiveFilters"
          @click="resetFilters"
          variant="secondary"
          class="whitespace-nowrap"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
          {{ t('restaurants.resetFilter') }}
        </BaseButton>
      </div>
    </div>

    <!-- Error Alert -->
    <div v-if="error" class="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-red-700 text-sm font-medium">{{ error }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden px-6 py-12 text-center">
      <div class="flex justify-center mb-4">
        <svg class="animate-spin h-8 w-8 text-primary-600" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
      <p class="text-gray-600">{{ t('restaurants.loading') }}</p>
    </div>

    <!-- Restaurants Table -->
    <div v-if="!isLoading" class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div v-if="filteredRestaurants.length > 0" class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-50 border-b border-gray-200">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{{ t('restaurants.columns.name') }}</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{{ t('restaurants.columns.manager') }}</th>
              <th v-if="showCompanyFilter" class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{{ t('restaurants.columns.company') }}</th>
              <th v-if="showPartnerFilter" class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">{{ t('restaurants.columns.partner') }}</th>
              <th class="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-20">{{ t('restaurants.columns.actions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="restaurant in filteredRestaurants" :key="restaurant.id" class="hover:bg-gray-50 transition-colors">
              <td class="px-6 py-4">
                <div>
                  <p class="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                    {{ restaurant.name }}
                    <span
                      v-if="!selectedStatus"
                      :class="['inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold', restaurant.status === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600']"
                    >
                      <span :class="['w-1.5 h-1.5 rounded-full', restaurant.status === 1 ? 'bg-green-600' : 'bg-gray-400']"></span>
                      {{ getStatusLabel(restaurant.status) }}
                    </span>
                    <span
                      v-if="restaurantTrialEnd(restaurant)"
                      class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200"
                    >
                      {{ t('forms.orderModes.trialUntil', { date: formatDateOnly(restaurantTrialEnd(restaurant)) }) }}
                    </span>
                  </p>
                  <p class="text-sm text-gray-500">{{ restaurant.address }}</p>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm">
                  <p class="text-gray-900 font-medium">{{ restaurant.manager?.name || '-' }}</p>
                  <p class="text-gray-600">{{ restaurant.manager?.email || '-' }}</p>
                  <p class="text-gray-500">{{ restaurant.manager?.mobile_no || '-' }}</p>
                </div>
              </td>
              <td v-if="showCompanyFilter" class="px-6 py-4">
                <div class="text-sm">
                  <p class="text-gray-900 font-medium">{{ restaurant.company?.company_name || '-' }}</p>
                  <p class="text-gray-500">{{ restaurant.company?.email || '-' }}</p>
                </div>
              </td>
              <td v-if="showPartnerFilter" class="px-6 py-4">
                <div class="text-sm">
                  <p class="text-gray-900 font-medium">{{ restaurant.partner?.company_name || '-' }}</p>
                  <p class="text-gray-500">{{ restaurant.partner?.email || '-' }}</p>
                </div>
              </td>
              <td class="px-6 py-4">
                <div class="flex gap-2">

                  <!-- View License Icon -->
                  <button
                    v-if="!isDatevAccountant"
                    @click="viewRestaurant(restaurant)"
                    :disabled="loadingRestaurantId === restaurant.id"
                    class="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    :title="t('restaurants.actions.viewTitle', { name: restaurant.name })"
                  >
                    <svg v-if="loadingRestaurantId === restaurant.id" class="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>

                  <!-- Licenses Icon (only if restaurant has licenses) -->
                  <button
                    v-if="can('licenses:list') && restaurant.licenses && restaurant.licenses.length > 0"
                    @click="openLicensesModal(restaurant)"
                    class="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="View Licenses"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </button>

                  <!-- Delete Icon -->
                  <!-- <button
                    @click="confirmDelete(restaurant)"
                    class="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    :title="t('restaurants.actions.deleteTitle', { name: restaurant.name })"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button> -->
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Table Footer with Info -->
      <div class="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <p class="text-sm text-gray-600">
          <span class="font-semibold text-gray-900">{{ filteredRestaurants.length }}</span>
          <span class="ml-2">{{ t(filteredRestaurants.length === 1 ? 'restaurants.found.singular' : 'restaurants.found.plural') }}</span>
        </p>
      </div>

      <!-- Empty State -->
      <div v-if="filteredRestaurants.length === 0" class="px-6 py-12 text-center">
        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2M5 21H3" />
        </svg>
        <p class="text-gray-500 mb-4">{{ t('restaurants.noRestaurants') }}</p>
        <ProtectedButton
          :permission="'restaurant:create'"
          @click="openForm"
          class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          show-disabled
          :denied-tooltip="t('restaurants.noPermissionCreate')"
        >
          {{ t('restaurants.addRestaurant') }}
        </ProtectedButton>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <!-- <DeleteConfirmationModal
      :is-open="showDeleteModal"
      :item-name="restaurantToDelete?.name || ''"
      item-type="Restaurant"
      :is-loading="isDeleting"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    /> -->

    <!-- Restaurant Licenses Modal -->
    <LicensesListModal
      v-if="licensesRestaurant"
      :restaurant="licensesRestaurant"
      @close="licensesRestaurant = null"
    />

  </div>
</template>

<style scoped></style>
